import { z } from "zod";
import { CommunityAsset } from "./asset.js";
import { CommunityAuthor } from "./author.js";
import { CommunityBehaviorTag, CommunityEventType, CommunityMomentStatus } from "./enums.js";
import { CommunityReactionSummary } from "./reaction.js";
import { CommunityPreferences } from "./settings.js";
export const CommunityMomentFeedItem = z.object({
    id: z.string().uuid(),
    type: z.literal("moment"),
    status: CommunityMomentStatus,
    author: CommunityAuthor,
    body: z.string(),
    behaviorTag: CommunityBehaviorTag.nullable(),
    asset: CommunityAsset.nullable(),
    // `source` is the row lineage: "member" for user-authored moments,
    // "system" for auto-published system events (member_joined,
    // streak_milestone, first_reflection, shared_content, shared_microstep).
    // Optional with a default so existing parsed fixtures (which omit it)
    // keep working; the mapper always populates it from the row.
    source: z.string().nullable().default("member"),
    // `eventType` + `eventPayload` are the discriminator + structured
    // payload for system and shared moments. Null/empty for plain member
    // moments; an object for system events. The mapper uses parseJsonb so
    // both raw SQL string results and parsed Drizzle results land on a
    // stable typed object.
    eventType: CommunityEventType.nullable().default(null),
    eventPayload: z.record(z.string(), z.unknown()).default({}),
    createdAt: z.string(),
    publishedAt: z.string().nullable(),
    reactions: z.array(CommunityReactionSummary),
    reportCount: z.number().int().nonnegative().default(0),
});
export const CreateCommunityMomentRequest = z.object({
    body: z.string().trim().min(1).max(1200),
    behaviorTag: CommunityBehaviorTag.nullable().optional(),
    sourceMicrostepId: z.string().uuid().nullable().optional(),
    assetId: z.string().uuid().nullable().optional(),
    enableSharing: z.boolean().optional(),
});
export const CreateCommunityMomentResponse = z.object({
    item: CommunityMomentFeedItem,
    preferences: CommunityPreferences,
});
/**
 * A member sharing a route milestone they or their group reached.
 *
 * The request names the milestone only. The publisher rebuilds the whole post
 * payload from stored progress, so the feed can never carry a route, a
 * waypoint, or a group name a client invented.
 */
export const ShareRouteMilestoneRequest = z.object({
    kind: z.enum(["waypoint", "completion"]).default("waypoint"),
    enrollmentId: z.string().uuid(),
    milestoneIndex: z.number().int().min(0).max(63),
    groupId: z.string().uuid().nullable().default(null),
});
export const ShareRouteMilestoneResponse = z.object({
    item: CommunityMomentFeedItem,
    preferences: CommunityPreferences,
});
/**
 * The stored shape of a `route_milestone` feed event. Both clients read this to
 * draw the small route map and its waypoint copy.
 */
export const RouteMilestoneEventPayload = z.object({
    /** Older posts predate the finish celebration and are all waypoints. */
    kind: z.enum(["waypoint", "completion"]).default("waypoint"),
    instanceId: z.string().uuid(),
    routeName: z.string(),
    milestoneIndex: z.number().int().nonnegative(),
    milestoneTitle: z.string(),
    milestoneDescription: z.string(),
    milestoneFraction: z.number(),
    milestoneOrdinal: z.number().int(),
    milestoneCount: z.number().int(),
    progressFraction: z.number(),
    distanceMeters: z.number().nullable(),
    routePath: z.array(z.object({ lat: z.number(), lng: z.number() })),
    routeMilestones: z.array(z.object({ fraction: z.number(), title: z.string(), description: z.string() })),
    subject: z.enum(["member", "group"]),
    groupName: z.string().nullable(),
    memberCount: z.number().int().nullable(),
});
/** A member-shared company-challenge rank crossing. */
export const ChallengeOvertakeEventPayload = z.object({
    eventId: z.string().uuid(),
    challengeId: z.string().uuid(),
    challengeName: z.string(),
    overtakenName: z.string(),
    overtakerSteps: z.number().int().nonnegative(),
    overtakenSteps: z.number().int().nonnegative(),
});
export const ShareChallengeOvertakeRequest = z.object({ eventId: z.string().uuid() });
export const ShareChallengeOvertakeResponse = z.object({
    item: CommunityMomentFeedItem,
    preferences: CommunityPreferences,
});
export const RemoveCommunityMomentResponse = z.object({
    ok: z.literal(true),
});
//# sourceMappingURL=moment.js.map